import { cloneElement, type MouseEvent, type ReactElement, useId } from 'react';
import { Tooltip, type TooltipPlacement } from '../components/Tooltip/Tooltip';
import styles from './PermissionGuard.module.css';
import { usePermission } from './permissionContext';

export interface PermissionGuardProps {
    /** The permission being asked for — never a role (AD #26; see `permissionContext.ts`). */
    readonly permission: string;
    /** The record the question is about, when the answer depends on one. */
    readonly resource?: string;
    /**
     * The control being guarded. A single element that accepts DOM props and is named by its
     * content — our `Button`, a link, or anything else that renders one such element. A form control
     * named by a `<label>` loses its name under this guard's self-reference in `aria-labelledby`, which
     * the guard writes over the child's own, so do not wrap one: join its reason with its own
     * `aria-labelledby="<label text id> <reason id>"`, as `AllRecordsTick` does with `Checkbox`'s
     * `labelId`.
     */
    readonly children: ReactElement;
    readonly reasonPlacement?: TooltipPlacement;
    readonly 'data-testid'?: string | undefined;
}

/** The props the guard writes onto the child. Kept honest by the cast at the one call site. */
interface GuardedProps {
    readonly id: string;
    readonly 'aria-disabled': true;
    readonly 'aria-labelledby': string;
}

/**
 * §9's Permission rendering rule as one shared component (FS-3 FR-C8) — "disable when the block is
 * contextual, hide when it is permanent for that role", which is a rule precisely because it is not
 * a per-screen judgement call and would drift within weeks if it were.
 *
 * **The disabled case, and each of its three parts is doing separate work.**
 *
 * 1. `aria-disabled="true"` and NOT the bare `disabled` attribute, so the control stays in the tab
 *    order. This is the whole point: a natively disabled control is skipped by screen readers and
 *    explains nothing, so a keyboard user meets a control they cannot reach and cannot ask about.
 * 2. The reason joins the ACCESSIBLE NAME through `aria-labelledby`, which lists the control itself
 *    first and then the reason node. Self-reference is the idiom that keeps the control's own name
 *    — a plain `aria-label` would REPLACE "Approve" with the reason, so the reader would announce
 *    why the user cannot do something without saying what that something was.
 * 3. Activation is blocked in the capture phase rather than trusted to the child. Our `Button`
 *    guards its own click when told it is disabled, but this component accepts any element, and a
 *    guard whose enforcement depended on the child's cooperation would be advisory.
 *
 * The `Tooltip` supplies §9's "surfaced on activation" half on hover and on focus — the reason a
 * sighted mouse user gets, where the accessible name is what a reader gets.
 *
 * **The allowed case draws the same elements as the disabled one**, with the tooltip switched off
 * and none of the three parts above. A contextual block moves while the screen is open (a lookup
 * answers, a save starts), and a control drawn bare when allowed and inside the tooltip when blocked
 * was a new element after each change, so the focus on it fell to the page (BUG-195). The child must
 * therefore take a ref onto its DOM element in both cases, as the `Tooltip` requires.
 *
 * **The hidden case renders nothing at all** — not an empty wrapper. Anything left behind would
 * still occupy a grid cell or a flex gap, which is how "hidden" controls end up as visible holes.
 */
export function PermissionGuard({
    permission,
    resource,
    children,
    reasonPlacement = 'top',
    'data-testid': testId,
}: PermissionGuardProps) {
    const decision = usePermission(permission, resource);
    const generatedId = useId();
    const controlId = `${generatedId}-control`;
    const reasonId = `${generatedId}-reason`;

    if (decision.kind === 'denied-permanent') {
        return null;
    }
    const reason = decision.kind === 'denied-contextual' ? decision.reason : null;

    function blockActivation(event: MouseEvent<HTMLSpanElement>) {
        // Capture phase, so this runs BEFORE the child's own handler rather than after it. By the
        // time a bubbled handler saw the event the child would already have done the thing.
        event.preventDefault();
        event.stopPropagation();
    }

    // The same child element either way, so React updates its attributes rather than replacing it.
    const control =
        reason === null
            ? children
            : cloneElement(children, {
                  id: controlId,
                  'aria-disabled': true,
                  // The control first, then the reason: a reader announces "Approve, only the Managing
                  // Director can approve a below-floor quote" in the order the user needs it.
                  'aria-labelledby': `${controlId} ${reasonId}`,
              } satisfies GuardedProps as Partial<unknown>);

    return (
        // `display: contents`, so the wrapper carries the handler without taking part in layout —
        // a guarded button in a flex row keeps its own spacing rather than gaining a box around it.
        <span
            className={styles.guard}
            data-testid={testId}
            onClickCapture={reason === null ? undefined : blockActivation}
        >
            <Tooltip content={reason} disabled={reason === null} placement={reasonPlacement}>
                {control}
            </Tooltip>
            {/*
             * Visually hidden rather than `hidden`: this node is referenced by `aria-labelledby`,
             * and while the accessible-name spec does read hidden referenced nodes, the clipped
             * form has no such caveat and behaves the same in every reader. It is deliberately NOT
             * `aria-hidden` — that WOULD remove it from the name computation, which is the one
             * thing it exists for.
             */}
            {reason === null ? null : (
                <span className={styles.reason} id={reasonId}>
                    {reason}
                </span>
            )}
        </span>
    );
}
